import { useEffect, useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { logout } from "scenes/Main.action";

const ACTIVITY_EVENTS = ["mousemove", "keydown", "click", "scroll", "touchstart"];
const INACTIVITY_TIMEOUT = 60 * 60 * 1000; // 1 hour in milliseconds
const CHECK_INTERVAL = 60 * 1000; // Check every minute
const STORAGE_KEY = "lastActivity";

export const resetLastActivity = () => {
    window.localStorage.setItem(STORAGE_KEY, Date.now().toString());
};

export const useInactivityLogout = () => {
    const dispatch = useDispatch();
    const checkIntervalRef = useRef(null);

    const isAuthenticated = useSelector((state) => state.mainReducer.authenticated);

    const updateLastActivity = useCallback(() => {
        const now = Date.now();
        window.localStorage.setItem(STORAGE_KEY, now.toString());
    }, []);

    const checkInactivity = useCallback(() => {
        const lastActivity = window.localStorage.getItem(STORAGE_KEY);
        if (!lastActivity) {
            updateLastActivity();
            return;
        }

        const elapsed = Date.now() - parseInt(lastActivity, 10);
        if (elapsed >= INACTIVITY_TIMEOUT) {
            dispatch(logout());
        }
    }, [dispatch, updateLastActivity]);

    useEffect(() => {
        if (!isAuthenticated) return;

        const handleActivity = () => {
            updateLastActivity();
        };

        ACTIVITY_EVENTS.forEach((event) => {
            window.addEventListener(event, handleActivity, { passive: true });
        });

        updateLastActivity();

        return () => {
            ACTIVITY_EVENTS.forEach((event) => {
                window.removeEventListener(event, handleActivity);
            });
        };
    }, [isAuthenticated, updateLastActivity]);

    useEffect(() => {
        if (!isAuthenticated) return;

        const handleStorageChange = (event) => {
            if (event.key === STORAGE_KEY && event.newValue) {
                checkInactivity();
            }
        };

        window.addEventListener("storage", handleStorageChange);

        return () => {
            window.removeEventListener("storage", handleStorageChange);
        };
    }, [isAuthenticated, checkInactivity]);

    useEffect(() => {
        if (!isAuthenticated) {
            if (checkIntervalRef.current) {
                clearInterval(checkIntervalRef.current);
                checkIntervalRef.current = null;
            }
            return;
        }

        checkIntervalRef.current = setInterval(checkInactivity, CHECK_INTERVAL);

        return () => {
            if (checkIntervalRef.current) {
                clearInterval(checkIntervalRef.current);
                checkIntervalRef.current = null;
            }
        };
    }, [isAuthenticated, checkInactivity]);
};

export const InactivityLogout = () => {
    useInactivityLogout();
    return null;
};
